import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../component/Header.js/Navbar/Navbar";
import Footer from "../component/Footer/Footer";
import {
  CalendarMonthOutlined,
  Person,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";

/* ── Styled ──────────────────────────────────────────── */
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

/* Hero banner */
const HeroBanner = styled.div`
  width: 100%;
  height: 55vh;
  position: relative;
  overflow: hidden;
  @media only screen and (max-width: 949px) {
    height: 38vw;
    min-height: 200px;
  }
`;
const HeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.62) 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 3rem 8rem;
  @media only screen and (max-width: 949px) {
    padding: 2rem;
  }
`;
const HeroTitle = styled.h1`
  color: white;
  font-size: 4rem;
  font-weight: 700;
  margin: 0 0 1rem;
  line-height: 1.2;
  max-width: 70%;
  @media only screen and (max-width: 949px) {
    font-size: 2.2rem;
    max-width: 100%;
    margin-bottom: 0.5rem;
  }
`;
const HeroMeta = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
`;
const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255,255,255,0.85);
  font-size: 1.3rem;
  svg { font-size: 1.5rem; }
`;

/* Content area */
const ContentWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 4rem 2rem 6rem;
  @media only screen and (max-width: 949px) {
    padding: 2.5rem 1.5rem 4rem;
  }
`;

const Breadcrumb = styled.div`
  font-size: 1.2rem;
  color: #888;
  margin-bottom: 2.5rem;
  a { color: #888; text-decoration: none; &:hover { color: black; } }
  span { margin: 0 0.4rem; }
`;

/* Rich text content from Quill */
const BlogContent = styled.div`
  font-size: 1.6rem;
  line-height: 1.85;
  color: #333;
  font-family: "Oswald", sans-serif;

  h1, h2, h3, h4 {
    font-weight: 700;
    margin: 2.5rem 0 1rem;
    color: black;
    line-height: 1.25;
  }
  h1 { font-size: 3rem; }
  h2 { font-size: 2.4rem; }
  h3 { font-size: 2rem; }

  p { margin: 0 0 1.6rem; }

  a {
    color: #511d00;
    text-decoration: underline;
    &:hover { color: #875031; }
  }

  ul, ol {
    padding-left: 2.5rem;
    margin: 0 0 1.6rem;
    li { margin-bottom: 0.5rem; }
  }

  blockquote {
    border-left: 4px solid #511d00;
    margin: 2rem 0;
    padding: 1rem 2rem;
    background: #fdf5f7;
    color: #555;
    font-style: italic;
  }

  pre, code {
    background: #f4f4f4;
    border-radius: 0.4rem;
    font-family: monospace;
    font-size: 1.4rem;
  }
  pre {
    padding: 1.2rem;
    overflow-x: auto;
    margin: 0 0 1.6rem;
  }
  code { padding: 0.1rem 0.4rem; }

  img {
    max-width: 100%;
    border-radius: 0.5rem;
    margin: 1.5rem 0;
  }

  strong { font-weight: 700; color: #222; }
  em { font-style: italic; }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eeeeee;
  margin: 3rem 0;
`;

/* Author card */
const AuthorCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: #fafafa;
  border: 1px solid #efefef;
  border-radius: 0.6rem;
  padding: 1.8rem 2rem;
  margin-bottom: 3rem;
`;
const AuthorAvatar = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  background-color: #511d00;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  font-weight: bold;
  flex-shrink: 0;
`;
const AuthorInfo = styled.div`
  p:first-child {
    font-size: 1.2rem;
    color: #888;
    margin: 0 0 0.2rem;
    text-transform: uppercase;
    letter-spacing: 0.1rem;
  }
  p:last-child {
    font-size: 1.8rem;
    font-weight: 700;
    color: black;
    margin: 0;
  }
`;

/* Related blogs */
const RelatedSection = styled.section`
  width: 90%;
  max-width: 1400px;
  margin: 0 auto 5rem;
  padding: 0 2rem;
  @media only screen and (max-width: 949px) {
    width: 95%;
    padding: 0 1rem;
  }
`;
const SectionTitle = styled.h2`
  font-size: 3rem;
  font-weight: 400;
  margin: 0 0 2.5rem;
  color: black;
  @media only screen and (max-width: 949px) { font-size: 2.2rem; }
`;
const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  @media only screen and (max-width: 949px) { grid-template-columns: repeat(2, 1fr); }
  @media only screen and (max-width: 600px) { grid-template-columns: 1fr; }
`;
const RelatedCard = styled.article`
  background: white;
  border-radius: 0.6rem;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  transition: transform 0.3s, box-shadow 0.3s;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  }
  img {
    width: 100%;
    height: 18rem;
    object-fit: cover;
    display: block;
  }
`;
const RelatedBody = styled.div`
  padding: 1.4rem;
  h3 {
    font-size: 1.6rem;
    font-weight: 600;
    margin: 0 0 0.6rem;
    color: black;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;
const RelatedReadMore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 1.2rem;
  color: #511d00;
  font-weight: 500;
  svg { font-size: 1.2rem; }
`;

const BackBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.3rem;
  color: #666;
  text-decoration: none;
  margin-bottom: 2rem;
  transition: color 0.2s;
  svg { font-size: 1.4rem; }
  &:hover { color: black; }
`;

/* ── Skeleton ── */
const shimmer = keyframes`
  100% { background-position: right -200px top 0; }
`;
const Sk = styled.div`
  height: ${(p) => p.h || "1.6rem"};
  width: ${(p) => p.w || "100%"};
  border-radius: 0.4rem;
  margin: ${(p) => p.mt || "0"} 0 0;
  background: linear-gradient(90deg, #ebebeb 0px, #f5f5f5 100px, #ebebeb 200px) left -200px top 0 / 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;

/* ── helpers ── */
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });

/* ── Component ───────────────────────────────────────── */
const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchBlog = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/blog/slug/${slug}`
        );
        const data = await res.json();
        if (data.success) {
          setBlog(data.data);
          fetchRelated(data.data._id);
        } else {
          navigate("/not-found");
        }
      } catch (e) {
        console.error("BlogDetail fetch error:", e);
        navigate("/not-found");
      }
      setLoading(false);
    };
    fetchBlog();
  }, [slug]);

  const fetchRelated = async (currentId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/blog/published`
      );
      const data = await res.json();
      if (data.success) {
        setRelated(data.data.filter((b) => b._id !== currentId).slice(0, 3));
      }
    } catch {}
  };

  return (
    <PageWrapper>
      <Navbar />

      {/* Hero */}
      {loading ? (
        <Sk h="55vh" mt="0" style={{ borderRadius: 0 }} />
      ) : (
        blog && (
          <HeroBanner>
            <HeroImg src={blog.image} alt={blog.title} />
            <HeroOverlay>
              <HeroTitle data-aos="fade-up">{blog.title}</HeroTitle>
              <HeroMeta>
                <MetaItem>
                  <CalendarMonthOutlined />
                  {fmtDate(blog.createdAt)}
                </MetaItem>
                <MetaItem>
                  <Person />
                  {blog.author}
                </MetaItem>
              </HeroMeta>
            </HeroOverlay>
          </HeroBanner>
        )
      )}

      {/* Main content */}
      <ContentWrapper>
        {loading ? (
          <>
            <Sk h="1.1rem" w="20%" mt="0" />
            <Sk h="2rem" w="40%" mt="2.5rem" />
            <Sk h="1.6rem" w="100%" mt="2rem" />
            <Sk h="1.6rem" w="95%" mt="0.8rem" />
            <Sk h="1.6rem" w="88%" mt="0.8rem" />
            <Sk h="1.6rem" w="92%" mt="0.8rem" />
            <Sk h="1.6rem" w="60%" mt="0.8rem" />
          </>
        ) : (
          blog && (
            <>
              <Breadcrumb>
                <Link to="/">Home</Link>
                <span>/</span>
                <Link to="/blogs">Blogs</Link>
                <span>/</span>
                {blog.title}
              </Breadcrumb>

              <AuthorCard>
                <AuthorAvatar>
                  {blog.author.charAt(0).toUpperCase()}
                </AuthorAvatar>
                <AuthorInfo>
                  <p>Written by</p>
                  <p>{blog.author}</p>
                </AuthorInfo>
              </AuthorCard>

              <BlogContent
                dangerouslySetInnerHTML={{ __html: blog.description }}
              />

              <Divider />

              <BackBtn to="/blogs">
                <ArrowBack /> Back to Blogs
              </BackBtn>
            </>
          )
        )}
      </ContentWrapper>

      {/* Related Blogs */}
      {!loading && related.length > 0 && (
        <RelatedSection>
          <SectionTitle data-aos="fade-right">More Articles</SectionTitle>
          <RelatedGrid>
            {related.map((b) => (
              <Link
                key={b._id}
                to={`/blogs/${b.slug}`}
                style={{ textDecoration: "none" }}
              >
                <RelatedCard data-aos="fade-up">
                  <img src={b.image} alt={b.title} />
                  <RelatedBody>
                    <h3>{b.title}</h3>
                    <MetaItem style={{ color: "#888", fontSize: "1.15rem", marginBottom: "0.8rem" }}>
                      <CalendarMonthOutlined style={{ fontSize: "1.3rem" }} />
                      {fmtDate(b.createdAt)}
                    </MetaItem>
                    <RelatedReadMore>
                      Read More <ArrowForward />
                    </RelatedReadMore>
                  </RelatedBody>
                </RelatedCard>
              </Link>
            ))}
          </RelatedGrid>
        </RelatedSection>
      )}

      <Footer />
    </PageWrapper>
  );
};

export default BlogDetail;
